import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { GenericServiceAttachmentDTO } from 'src/app/core/models/shared/digital-services/GenericServiceAttachmentDTO';
import { TranslateService } from '@ngx-translate/core';
import { ServiceRequestTypeCodeEnum } from '../../core/enums/service-request-type-code.enum';
import { ResponseModel } from '../../core/models/shared/CommonResponseDTO';
import {
  CustomerRequestActionsEnum,
  ServiceMainTransactionData,
  WorkflowActionsCodesEnum,
  WorkflowStepStatusCodesEnum,
} from '../../core/models/common/ServiceMainTransactionData';
import { ServiceRequestType } from 'src/app/core/models/common/ServiceRequestType';
import { RequestAttachment } from '../../core/models/common/RequestAttachment';
import {
  Employee,
  RegisteredUserTypesEnum,
} from '../../core/models/profile/RegisteredUser';
import { ToastrService } from 'ngx-toastr';
import { UtilService } from '../../core/utilities/util.service';
import { ServiceMetadataGetCustomDetailService } from '../../core/api-services/portal/service-metadata-get-custom-details.service';
import { TemplateService } from '../../core/api-services/common/template.service';
import {
  ApplicantIdentityTypeEnum,
  CustomerApplicant,
} from '../../core/models/customer/CustomerApplicant';
import { ServiceRequestChannelTypesEnum } from '../../core/enums/ServiceRequestChannelTypesEnum';
import { Platform } from '@angular/cdk/platform';
import { GetServiceExternalIntegrationService } from 'src/app/core/api-services/common/ExternalIntegration/get-service-external-integration.service';
import { RequestPurposeTypeEnum } from '../../core/enums/RequestPurposeTypeEnum';
import { HasPaymentDto } from '../../core/models/common/ServiceValidationResponseDto';
import {
  AttachmentFileDetailsUploaded,
  AttachmentFormObject,
} from 'src/app/core/models/common/AttachmentFormObject';
import { BackendSystemEnum } from '../shared-services-steps-module/congratulations/congratulations.component';
import { ApplicantStateObjectType } from './ApplicantStateObjectType';
import { AttachmentsStateObjectType } from './AttachmentsStateObjectType';
import { ReviewObjectType } from './ReviewObjectType';
import { CongratulationsObjectType } from './CongratulationsObjectType';
import {
  PortalServiceDetailsCustomDetailsResponse,
  PortalServiceDetailsLookupDto,
} from 'src/app/core/models/portal/GetAllServicesDetailResponse';
import { ServiceMetaData } from 'src/app/core/models/shared/ServiceMetaData';
import { PortalServiceDetailsMapperService } from 'src/app/core/api-services/portal/portal-service-details-mapper.service';

declare let TimeMe: any;

export abstract class WorkflowService<T> {
  renderAutoFocusElement: boolean = true;
  retrievedServiceObjectDto: T;
  currentServiceObjectDto: T;
  retrievedServiceRequestTransaction: ServiceMainTransactionData;
  currentServiceRequestTransaction: ServiceMainTransactionData;
  submittedActionCode: WorkflowActionsCodesEnum;
  lastSubmittedActionCode: WorkflowActionsCodesEnum =
    WorkflowActionsCodesEnum.ReturnToCustomer;
  requestTypeCode: ServiceRequestTypeCodeEnum;
  isStaticPayment: boolean;
  requestTypeObject: ServiceRequestType;
  serviceRequestTransactionId: number;
  serviceId: number;
  requestTypeId: number;
  feesFromPreviousYears: number;
  buyerRegisteredUserId: number;
  eServiceResponseData: ServiceMetaData;
  // eServiceResponseDataEnglish: serviceDetailByDigitalNumberResponse.ResponseData;
  // eServiceResponseDataArabic: serviceDetailByDigitalNumberResponse.ResponseData;
  hasPaymentDto: HasPaymentDto;

  globalObject: {
    ApplicantConfiguration: ApplicantStateObjectType;
    ServiceDetailsConfiguration: any;
    AttachmentsConfiguration: AttachmentsStateObjectType;
    ReviewConfiguration: ReviewObjectType;
    Congratulations: CongratulationsObjectType;
    Response: ServiceMainTransactionData;
  };

  isSendBack: boolean;
  abstract currentStep: string;
  abstract steps: string[];
  public stepsCount: number = 4;
  protected abstract initializeServiceDetailForm(): any;
  public abstract getServiceUrlKeyWord(): string;
  public abstract isShowTermsAndConditions(): boolean;
  abstract isPendingOnPayment(): boolean;
  abstract isWaitingForCustomerAction(): boolean;
  abstract prepareServiceDetailToPost(): T;
  abstract loadAttachments(): Observable<
    ResponseModel<GenericServiceAttachmentDTO[]>
  >;
  abstract setRetrievedServiceRequestTransactionObject(): void;
  public pageNumber: number = 1;

  public get isRequestTypeCancelActivity(): boolean {
    return this.requestTypeCode == ServiceRequestTypeCodeEnum.CancelActivity;
  }

  public get isRequestTypeCancelLicense(): boolean {
    return this.requestTypeCode == ServiceRequestTypeCodeEnum.CancelLicense;
  }

  public get isRequestTypeAddActivity(): boolean {
    return this.requestTypeCode == ServiceRequestTypeCodeEnum.AddActivity;
  }

  public get isRequestTypeIssue(): boolean {
    return this.requestTypeCode == ServiceRequestTypeCodeEnum.Issue;
  }

  public get isRequestTypeRenew(): boolean {
    return this.requestTypeCode == ServiceRequestTypeCodeEnum.Renew;
  }

  public get isStatusPaymentRequired(): boolean {
    return (
      this.retrievedServiceRequestTransaction?.statusCode ==
      WorkflowStepStatusCodesEnum.PaymentRequired
    );
  }

  protected lang: string;

  public get isArabic(): boolean {
    return this.lang == 'ar';
  }

  public get isEnglish(): boolean {
    return this.lang == 'en';
  }

  constructor(
    protected translateService: TranslateService,
    protected route: ActivatedRoute,
    protected router: Router,
    protected toastr: ToastrService,
    protected utilService: UtilService,
    protected serviceDetailByDigitalNumberService: ServiceMetadataGetCustomDetailService,
    protected templateService: TemplateService<T>,
    protected platform: Platform,
    protected IsCompanyService: boolean,
    protected getServiceExternalIntegrationService: GetServiceExternalIntegrationService,
    protected portalServiceDetailsMapper: PortalServiceDetailsMapperService
  ) {
    this.subscribeToLanguageChange();

    if (this.validateUserProfile()) {
      this.validateRequest();
      this.initializeGlobalObject();
      this.startTimer();
    }
  }

  private subscribeToLanguageChange() {
    this.lang = this.translateService.currentLang;
    this.translateService.onLangChange.subscribe((langChangeEvent) => {
      this.lang = langChangeEvent.lang;

      this.getServiceDetailByDigitalNumber();
    });
  }

  public get backendSystem(): BackendSystemEnum {
    return BackendSystemEnum.DigitalServices;
  }

  private startTimer(): void {
    TimeMe.initialize({
      currentPageName: this.getServiceUrlKeyWord(), // current page
      idleTimeoutInSeconds: 600,
    });

    TimeMe.startTimer();
  }

  private getTimeTakenByCustomer(): number {
    const key = this.getServiceUrlKeyWord();

    const result = Math.floor(TimeMe.getTimeOnPageInSeconds(key) || 0);

    return result;
  }

  isServiceRequestTransactionInProgress(): boolean {
    return (
      this.retrievedServiceRequestTransaction?.statusCode ==
        WorkflowStepStatusCodesEnum.NewRequest ||
      this.retrievedServiceRequestTransaction?.isDraft == true ||
      this.retrievedServiceRequestTransaction?.statusCode ==
        WorkflowStepStatusCodesEnum.WaitingCustomerAction
    );
  }

  public postData(
    action: CustomerRequestActionsEnum
  ): Observable<ResponseModel<ServiceMainTransactionData>> {
    this.currentServiceObjectDto = this.prepareServiceObjectForPostData(action);
    if (
      this.retrievedServiceRequestTransaction != null &&
      (this.retrievedServiceRequestTransaction.statusCode ==
        WorkflowStepStatusCodesEnum.NewRequest ||
        this.retrievedServiceRequestTransaction.isDraft == true ||
        this.retrievedServiceRequestTransaction.statusCode ==
          WorkflowStepStatusCodesEnum.WaitingCustomerAction ||
        this.retrievedServiceRequestTransaction.statusCode ==
          WorkflowStepStatusCodesEnum.PaymentRequired)
    ) {
      this.currentServiceRequestTransaction.id =
        this.retrievedServiceRequestTransaction.id;
      return this.templateService.update(this.currentServiceObjectDto);
    } else {
      return this.templateService.create(this.currentServiceObjectDto);
    }
  }

  private prepareServiceObjectForPostData(
    action: CustomerRequestActionsEnum
  ): T {
    let serviceMainTransactionData = new ServiceMainTransactionData();
    // ########################## Prepare applicant ##########################
    if (
      this.utilService.getUserProfile().registeredUserType ===
      RegisteredUserTypesEnum.Authority
    ) {
      const applicantStateObject = this.globalObject.ApplicantConfiguration;
      let applicant = this.prepareApplicantObjectForPostData();

      applicant.registerUserId = +this.utilService.getUserProfile().id;
      applicant.identityType = ApplicantIdentityTypeEnum.EmiratesId;

      serviceMainTransactionData.requestPurposeTypeId =
        applicantStateObject.form.get('selectedRequestPurposeType').value;
      serviceMainTransactionData.serviceApplicantData = applicant;
      serviceMainTransactionData.serviceApplicantId = applicant.id;
    }

    //Check One of the External Integration Exists.
    if (
      this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration !=
      null
    ) {
      serviceMainTransactionData.serviceExternalIntegrationData =
        this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration;
      if (serviceMainTransactionData.serviceExternalIntegrationData) {
        serviceMainTransactionData.serviceExternalIntegrationData.envPermitLicenseDetailData =
          null;
      }
    }

    //Set is Disable individual User
    if (
      this.utilService.getUserProfile().people != null &&
      this.utilService.getUserProfile().people.isDisableUser
    ) {
      serviceMainTransactionData.isFromPeopleOfDetermination = true;
    }

    serviceMainTransactionData.language = null;

    serviceMainTransactionData.serviceId = this.serviceId;
    serviceMainTransactionData.registeredUserId =
      +this.utilService.getUserProfile().id;

    serviceMainTransactionData.serviceRequestTypeId = this.requestTypeId;
    serviceMainTransactionData.serviceRequestChannelType =
      this.getPlatformBrowser();
    serviceMainTransactionData.registrationType = 'Authority';
    serviceMainTransactionData.language = this.translateService.currentLang;

    if (this.buyerRegisteredUserId) {
      serviceMainTransactionData.buyerRegisteredUserId =
        this.buyerRegisteredUserId;
    }

    //Set Employee Id if exists [Employee login on behalf of customer].
    let employeeObj: Employee = this.utilService.getEmployeeProfile();
    if (employeeObj) {
      serviceMainTransactionData.employeeId = employeeObj.employeeId;
    }

    serviceMainTransactionData.requestAttachments =
      this.prepareAttachmentsToPost();

    serviceMainTransactionData.customerRequestAction = action;
    this.currentServiceRequestTransaction = serviceMainTransactionData;

    this.currentServiceRequestTransaction.elapsedTime = {
      time: this.getTimeTakenByCustomer(),
    };

    return this.prepareServiceDetailToPost();
  }

  private prepareAttachmentsToPost(): RequestAttachment[] {
    const attachments: RequestAttachment[] = [];
    const attachmentsInForm =
      this.globalObject.AttachmentsConfiguration.form.get('Attachments')
        .value as AttachmentFormObject[];

    for (let attachment of attachmentsInForm) {
      if (
        attachment &&
        attachment.attachmentId &&
        attachment.attachmentFiles &&
        attachment.attachmentFiles.length > 0
      ) {
        for (let attachmentFile of attachment.attachmentFiles) {
          let att = new RequestAttachment();
          att.attachmentID = attachment.attachmentId;
          att.fileAttachmentId = attachmentFile.storedFileAttachmentDBId;
          att.isNewMessage = false;
          att.serviceCatalogId = this.serviceId;
          att.employeeFeedbackMessage = attachmentFile.employeeFeedbackMessage;
          attachments.push(att);
        }
      }
    }

    const serviceDetailsStepAttachments =
      this.prepareServiceDetailsStepAttachmentsToPost();
    attachments.push(...serviceDetailsStepAttachments);

    return attachments;
  }

  private prepareApplicantObjectForPostData(): CustomerApplicant {
    let applicant = {} as CustomerApplicant;
    const applicantStateObject = this.globalObject.ApplicantConfiguration;

    if (applicantStateObject.isNewApplicant) {
      applicant.email = applicantStateObject.form.get('EmailID').value;
      applicant.identityNumber =
        applicantStateObject.form.get('EmiratesId').value;
      applicant.mobile = applicantStateObject.form.get('MobileNumber').value;
      applicant.nameEn =
        applicantStateObject.findByEmirateIdResponse.Data.ParticipantToRegister.NameEn;
      applicant.nameAr =
        applicantStateObject.findByEmirateIdResponse.Data.ParticipantToRegister.NameAr;
      applicant.area =
        applicantStateObject.findByEmirateIdResponse.Data.ParticipantToRegister.Area;
      applicant.address =
        applicantStateObject.findByEmirateIdResponse.Data.ParticipantToRegister.ParticipantAddress;
      applicant.emirate =
        applicantStateObject.findByEmirateIdResponse.Data.ParticipantToRegister.Emirate;
      applicant.country =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.Country;
      applicant.countryId =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.CountryId;
      if (
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister
          .BirthDate
      ) {
        let from =
          applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.BirthDate?.split(
            '-'
          );
        let date = new Date(+from[2], +from[1] - 1, +from[0]);
        applicant.birthDate = date;
      }
      applicant.birthPlace =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.BirthPlace;
      applicant.sex =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.Sex;
      applicant.specializationID =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.SpecializationID;
      applicant.occupationID =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.OccupationID;
      applicant.maritalStatusID =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.MaritalStatusID;
      applicant.religionID =
        applicantStateObject.findByEmirateIdResponse.Data.PersonToRegister.ReligionID;
    } else if (applicantStateObject.isEditMode) {
      const applicantInForm = applicantStateObject.form.get('ApplicantName')
        .value as CustomerApplicant;
      applicant = applicantInForm;
      applicant.mobile = applicantStateObject.form.get('MobileNumber').value;
      applicant.email = applicantStateObject.form.get('EmailID').value;
    } else {
      const applicantInForm = applicantStateObject.form.get('ApplicantName')
        .value as CustomerApplicant;
      applicant = applicantInForm;
    }

    return applicant;
  }

  private prepareServiceDetailsStepAttachmentsToPost(): RequestAttachment[] {
    ///as some service doesn't have service details step attachments used (|| [])
    const attachmentFiles: AttachmentFileDetailsUploaded[] =
      this.globalObject.ServiceDetailsConfiguration.attachmentFiles || [];

    return attachmentFiles.map((serviceStepAttachment) => {
      let att = new RequestAttachment();
      att.attachmentID = serviceStepAttachment.attachment.identity as number;
      att.fileAttachmentId =
        serviceStepAttachment.file.storedFileAttachmentDBId;
      att.isNewMessage = false;
      att.serviceCatalogId = this.serviceId;
      att.employeeFeedbackMessage =
        serviceStepAttachment.file.employeeFeedbackMessage;

      return att;
    });
  }

  private getPlatformBrowser() {
    let requestChannel = ServiceRequestChannelTypesEnum.Web;
    if (this.platform.IOS) {
      requestChannel = ServiceRequestChannelTypesEnum.IOS;
    } else if (this.platform.ANDROID) {
      requestChannel = ServiceRequestChannelTypesEnum.Andriod;
    } else if (this.platform.EDGE) {
      requestChannel = ServiceRequestChannelTypesEnum.EDGE;
    } else if (this.platform.FIREFOX) {
      requestChannel = ServiceRequestChannelTypesEnum.FIREFOX;
    } else if (this.platform.SAFARI) {
      requestChannel = ServiceRequestChannelTypesEnum.SAFARI;
    } else if (this.platform.TRIDENT) {
      requestChannel = ServiceRequestChannelTypesEnum.TRIDENT;
    } else if (this.platform.WEBKIT) {
      requestChannel = ServiceRequestChannelTypesEnum.WEBKIT;
    }

    return requestChannel;
  }

  saveAsDraft() {
    this.postData(CustomerRequestActionsEnum.OnDraft).subscribe((response) => {
      this.router.navigate(['./home']).then(() => {
        this.toastr.info(
          this.translateService.instant('CongratulationsApplyDraftRequest', {
            RequestNumber: response.responseData.serviceRequestNumber,
          })
        );
      });
    });
  }

  public resetAttachment() {
    const attachmentConfigurations = this.globalObject.AttachmentsConfiguration;
    attachmentConfigurations.form.reset();
    (<FormArray>attachmentConfigurations.form.get('Attachments')).clear();
    attachmentConfigurations.IsAttachmentRetrieved = false;

    // Reset Retrieved External Integration Data.
    this.resetRetrievedExternalIntegrationData();
  }

  private initializeGlobalObject(): void {
    this.globalObject = {
      ApplicantConfiguration: Object.assign({}, this.ApplicantStateObject),
      ServiceDetailsConfiguration: this.initializeServiceDetailForm(),
      AttachmentsConfiguration: Object.assign({}, this.AttachmentsStateObject),
      ReviewConfiguration: Object.assign({}, this.ReviewStateObject),
      Congratulations: Object.assign({}, this.CongratulationsStateObject),
      Response: {} as ServiceMainTransactionData,
    };
  }
  abstract submitNewRequest(router: Router): void;
  abstract showSubmitNewRequestButton: boolean;
  // should be implemented by each service
  abstract isServiceDetailAutoRenewal(): boolean;

  isValidAutoRenewal(): boolean {
    if (!this.requestTypeObject.isAutoRenewal) return false;

    return this.isServiceDetailAutoRenewal();
  }

  validateUserProfile(): boolean {
    const userProfile = this.utilService.getUserProfile();
    if (
      userProfile == null ||
      userProfile.registeredUserAddress == null ||
      userProfile.registeredUserAddress == ''
    ) {
      this.router.navigate(['/home'], { queryParams: { dashboard: 'true' } });
      console.log(
        "************** The Participant doesn't have address *********************"
      );
      this.toastr.warning(
        this.translateService.instant(
          'YouDoNotHaveAddressPleaseEditYourProfile'
        )
      );
      return false;
    }
    //##########End Validate address of the Participant ####################################

    //########## Validate Registered User is a company and not individual ######################
    if (
      userProfile != null &&
      this.IsCompanyService &&
      userProfile.registeredUserType != RegisteredUserTypesEnum.Authority
    ) {
      console.log(
        '************** The Participant is individual*********************'
      );
      this.toastr
        .warning(
          this.translateService.instant(
            'ItIsNotAllowedToProvideThisServiceToIndividuals'
          )
        )
        .onShown.subscribe((a) => {
          this.router.navigate(['/home'], {
            queryParams: { dashboard: 'true' },
          });
        });
      return false;
    }
    return true;
    //########## End Validate Registered User is a company and not individual ##############
  }

  validateRequest() {
    let queryParamsSnapshot = this.route.snapshot.queryParams;
    this.setRequestType(queryParamsSnapshot['requestTypeCode']);
    this.setRequestId(queryParamsSnapshot['requestId']);
    this.setActionCode(queryParamsSnapshot['actionCode']);

    if (
      !Object.values(ServiceRequestTypeCodeEnum).includes(this.requestTypeCode)
    ) {
      this.toastr
        .warning(
          this.translateService.instant('YouAccessedTheServiceWithImproperWay')
        )
        .onShown.subscribe((a) => {
          this.router.navigate(['/']);
        });
      return;
    }

    this.route.queryParams.subscribe((params) => {
      if (params['requestTypeCode']) {
        //incase the customer changed requestType queryParams modified after initial redirection
        if (
          this.requestTypeCode &&
          params['requestTypeCode'].toString() != this.requestTypeCode
        ) {
          this.toastr
            .warning(
              this.translateService.instant(
                'YouAccessedTheServiceWithImproperWay'
              )
            )
            .onShown.subscribe((a) => {
              this.router.navigate(['/']);
            });
          return;
        }

        this.setRequestType(params['requestTypeCode']);
      }
      if (params['requestId']) {
        this.setRequestId(params['requestId']);
      }

      if (!params['requestId']) {
        if (this.serviceRequestTransactionId) {
          this.router.navigate([], {
            queryParams: { requestNumber: this.serviceRequestTransactionId },
            queryParamsHandling: 'merge',
          });
        } else if (this.requestTypeCode != ServiceRequestTypeCodeEnum.Issue) {
          this.router.navigate(['/']);
          this.toastr
            .warning(
              this.translateService.instant(
                'YouAccessedTheServiceWithImproperWay'
              )
            )
            .onShown.subscribe((a) => {
              this.router.navigate(['/']);
            });
          return;
        }
      }
    });

    this.templateService
      .validate(this.requestTypeCode, this.serviceRequestTransactionId)
      .subscribe(
        (response) => {
          this.retrievedServiceObjectDto =
            response.responseData.serviceObjectDto;
          this.requestTypeId = response.responseData.serviceRequestTypeDto.id;
          this.requestTypeObject = response.responseData.serviceRequestTypeDto;
          this.serviceId =
            response.responseData.serviceRequestTypeDto.serviceData.id;

          if (
            !this.utilService.isEmployeeHasAccessOnTheService(this.serviceId)
          ) {
            this.toastr
              .warning(
                this.translateService.instant(
                  'MinistryEmployeeDoNotHavePrivilegeOnService'
                )
              )
              .onShown.subscribe((a) => {
                this.router.navigate(['/']);
              });
            return;
          }

          let staticPayment = false;
          if (
            response.responseData.hasPaymentDto &&
            response.responseData.hasPaymentDto.hasPayment === true &&
            response.responseData.hasPaymentDto.isVariablePayment === false
          ) {
            staticPayment = true;
            this.setIsStaticPayment(staticPayment);

            if (response.responseData.hasPaymentDto.staticServiceFees) {
              this.setTotalPayment(
                response.responseData.hasPaymentDto.staticServiceFees -
                  response.responseData.hasPaymentDto
                    .staticRenewalFeesForPreviousYears
              );
            }

            this.hasPaymentDto = response.responseData.hasPaymentDto;
          }
          this.setRetrievedServiceRequestTransactionObject();
          if (this.retrievedServiceRequestTransaction) {
            this.setRetrievedApplicantInfoAndAttachment();
            if (
              this.requestTypeCode == ServiceRequestTypeCodeEnum.Renew ||
              this.requestTypeCode == ServiceRequestTypeCodeEnum.CancelLicense
            ) {
              if (staticPayment) {
                this.feesFromPreviousYears =
                  response.responseData.hasPaymentDto.staticRenewalFeesForPreviousYears;
              } else {
                this.templateService
                  .getRenewalFeesForPreviousYears(this.serviceId)
                  .subscribe((response) => {
                    this.feesFromPreviousYears = response.responseData;
                  });
              }
            }
          }
          this.getServiceDetailByDigitalNumber();
          this.getServiceExternalIntegrationDataByRequestTransactionId();
          this.handleRequestPurposeTypeValidation();
          this.skipApplicant();
        },
        (error) => {
          this.router.navigate(['/home']);
        }
      );
  }
  skipApplicant(): void {
    if (
      this.utilService.getUserProfile().registeredUserType ===
      RegisteredUserTypesEnum.Person
    ) {
      this.reInitializeApplicantForm();
      this.moveForward(this.router, true);
    }
  }

  reInitializeApplicantForm(): void {
    this.globalObject.ApplicantConfiguration.form = new FormGroup({
      ApplicantName: new FormControl(null),
      EmiratesId: new FormControl(null),
      Name: new FormControl(null),
      MobileNumber: new FormControl(null),
      EmailID: new FormControl(null),
      selectedRequestPurposeType: new FormControl(null),
    });
  }

  protected ApplicantStateObject: ApplicantStateObjectType = {
    form: new FormGroup({
      ApplicantName: new FormControl(null, Validators.required),
      EmiratesId: new FormControl(null),
      Name: new FormControl(null),
      MobileNumber: new FormControl(null),
      EmailID: new FormControl(null),
      selectedRequestPurposeType: new FormControl(null),
    }),
    isNewApplicant: false,
    findByEmirateIdResponse: null,
    isValidEmiratesId: false,
    enableStep: false,
    showAddNewApplicantButton: false,
    showEditApplicantButton: false,
    showDetailsApplicantButton: true,
    showDetails: false,
    isEditMode: false,
    showRequestPurposeType: false,
    selectedRequestPurposeTypeCode: null,
    url: '/services/' + this.getServiceUrlKeyWord() + '/applicantinformation',
  };

  protected AttachmentsStateObject: AttachmentsStateObjectType = {
    Attachments: [], //Coming from get attachments which implemented in each service detail [GenericService Attachment DTO]
    RetrievedAttachments: [], // Retrieved Attachments  [Service Request Attachments]
    IsAttachmentRetrieved: false, // if you need to reset attachment from service details page do we have to call reset attachment and sit this param with false.
    form: new FormGroup({
      Attachments: new FormArray([]), //This the form construct from custom object inside attachment module [AttachmentFormObject]
    }),
    ServiceExternalIntegration: null,
    enableStep: false,

    url: '/services/' + this.getServiceUrlKeyWord() + '/attachments',
  };

  protected ReviewStateObject: ReviewObjectType = {
    form: new FormGroup({
      AcceptTerms: new FormControl(null),
      AutoRenewal: new FormControl(null),
      TotalPayment: new FormControl({ value: null, disabled: true }),
    }),
    enableStep: false,
    url: '/services/' + this.getServiceUrlKeyWord() + '/review',
    isSkipPaymentEnabled: false,
  };

  protected CongratulationsStateObject: CongratulationsObjectType = {
    form: {
      valid: false,
    },
    url: '/services/' + this.getServiceUrlKeyWord() + '/congrats',
  };

  public getTotalPayment(): number {
    return this.globalObject.ReviewConfiguration.form.get('TotalPayment').value;
  }

  public setTotalPayment(totalPayment: number): void {
    this.globalObject.ReviewConfiguration.form
      .get('TotalPayment')
      .setValue(totalPayment);
  }

  setRetrievedApplicantInfoAndAttachment(): void {
    if (this.retrievedServiceRequestTransaction.serviceApplicantData) {
      (this.globalObject.ApplicantConfiguration.form as FormGroup)
        .get('ApplicantName')
        .setValue(this.retrievedServiceRequestTransaction.serviceApplicantData);
    }
    (this.globalObject.ApplicantConfiguration.form as FormGroup)
      .get('selectedRequestPurposeType')
      .setValue(this.retrievedServiceRequestTransaction.requestPurposeTypeId);
    this.globalObject.AttachmentsConfiguration.RetrievedAttachments =
      this.retrievedServiceRequestTransaction.requestAttachments;
  }

  protected async getServiceDetailByDigitalNumber(): Promise<void> {
    const response = await this.serviceDetailByDigitalNumberService
      .getServiceMetadataCustomDetails(this.serviceId.toString(), this.lang)
      .toPromise();

    if (response && response.data) {
      this.eServiceResponseData =
        this.portalServiceDetailsMapper.mapMetadata(response);
    }
  }

  isRoutingDirectionForward(previousStep: string): boolean {
    const previousStepIndex = this.steps.indexOf(previousStep);
    const currentIndex = this.steps.indexOf(this.currentStep);

    return previousStepIndex <= currentIndex;
  }

  setRequestType(requestType: ServiceRequestTypeCodeEnum): void {
    this.requestTypeCode = requestType;
  }

  setIsStaticPayment(isStaticPayment: boolean): void {
    this.isStaticPayment = isStaticPayment;
  }

  setRequestId(requestId: number) {
    this.serviceRequestTransactionId = requestId;
  }

  setActionCode(actionCode: WorkflowActionsCodesEnum) {
    this.submittedActionCode = actionCode;
  }

  goToStep(step: string, router: Router = null) {
    if (this.globalObject.Congratulations.form.valid == false) {
      const url = (<any>this.globalObject)[step].url;
      if (url) {
        this.router.navigate([url], { queryParamsHandling: 'preserve' });
      }
    }
  }

  redirectTo(uri: string, router: Router) {
    router
      .navigateByUrl('/', { skipLocationChange: true })
      .then(() => router.navigate([uri]));
  }

  moveBack(router: Router = null, isSkipped: boolean = false) {
    const curStep = this.currentStep;
    const curIndex = this.steps.indexOf(curStep);
    if (curIndex > 0) {
      const url = (<any>this.globalObject)[this.steps[curIndex - 1]].url;
      if (url) {
        if (!isSkipped) this.pageNumber--;
        this.router.navigate([url], { queryParamsHandling: 'preserve' });
      }
    }
  }

  moveForward(router: Router, isSkipped: boolean = false) {
    const curStep = this.currentStep;
    const curIndex = this.steps.indexOf(curStep);
    (<any>this.globalObject)[this.steps[curIndex]].enableStep = true;
    if (curIndex < this.steps.length) {
      const url = (<any>this.globalObject)[this.steps[curIndex + 1]].url;
      if (url) {
        if (!isSkipped) this.pageNumber++;
        router.navigate([url], { queryParamsHandling: 'preserve' });
      }
    }
  }

  public getNextStepStateObject() {
    const curStep = this.currentStep;
    const curIndex = this.steps.indexOf(curStep);
    if (curIndex < this.steps.length) {
      return (<any>this.globalObject)[this.steps[curIndex + 1]];
    }

    return null;
  }

  public isValidAndEnabledStep(step: string): boolean {
    const curIndex = this.steps.indexOf(step);
    if (this.globalObject && curIndex >= 0) {
      return (
        (<any>this.globalObject)[this.steps[curIndex]].enableStep == true &&
        (<any>this.globalObject)[this.steps[curIndex]].form.valid == true
      );
    }

    return false;
  }

  isValidStep(step: string): boolean {
    const curIndex = this.steps.indexOf(step);
    if (curIndex >= 0) {
      return (<any>this.globalObject)[this.steps[curIndex]].form.valid == true;
    }

    return false;
  }

  checkAllPerviousStepsValidation(): boolean {
    const curStep = this.currentStep;
    let curIndex = this.steps.indexOf(curStep);
    if (curIndex <= 0) {
      return true;
    }

    let result = true;
    while (--curIndex >= 0) {
      if (!this.isValidStep(this.steps[curIndex])) {
        result = false;
        break;
      }
    }

    return result;
  }

  showBackArrow(): boolean {
    const curStep = this.currentStep;
    const curIndex = this.steps.indexOf(curStep);
    if (curIndex <= 0 || curIndex >= this.steps.length - 1) {
      return false;
    }

    return true;
  }

  getServiceName() {
    const requestType = this.requestTypeObject;
    if (requestType) {
      const requestTypeName =
        this.translateService.currentLang == 'ar'
          ? requestType.serviceData?.nameAr
          : requestType.serviceData?.nameEn;
      return requestTypeName;
    }

    return '';
  }

  getPossibleStepAfterLastValidStep(curStep: string): string {
    const curIndex = this.steps.indexOf(curStep);
    if (
      // check if this is the first step
      curIndex <= 0 ||
      // check if previous step is valid and entered
      ((<any>this.globalObject)[this.steps[curIndex - 1]].form.valid == true &&
        (<any>this.globalObject)[this.steps[curIndex - 1]].enableStep ==
          true) ||
      // check if the current step is valid and entered
      ((<any>this.globalObject)[this.steps[curIndex]].form.valid &&
        (<any>this.globalObject)[this.steps[curIndex]].enableStep == true)
    ) {
      return curStep;
    } else {
      return this.getPossibleStepAfterLastValidStep(this.steps[curIndex - 1]);
    }
  }

  public getPostDataResponseObject(): ServiceMainTransactionData {
    const Response = this.globalObject.Response;

    return Response;
  }

  public showPayNow(): boolean {
    return (
      this.isStatusPaymentRequired ||
      (this.requestTypeObject.hasFrontPayment &&
        (this.getTotalPayment() > 0 || this.feesFromPreviousYears > 0) &&
        this.globalObject.ReviewConfiguration.isSkipPaymentEnabled == false)
    );
  }

  public showPayLater(): boolean {
    return (
      this.isStatusPaymentRequired == false &&
      this.requestTypeObject.hasFrontPayment &&
      (this.getTotalPayment() > 0 || this.feesFromPreviousYears > 0) &&
      this.globalObject.ReviewConfiguration.isSkipPaymentEnabled == false
    );
  }

  public showSubmit(): boolean {
    return !this.showPayNow() && !this.showSubmitSkipPayment();
  }

  public showSubmitSkipPayment(): boolean {
    return (
      this.requestTypeObject.canSkipPayment &&
      this.isStatusPaymentRequired == false &&
      (!this.showPayNow() ||
        this.globalObject.ReviewConfiguration.isSkipPaymentEnabled)
    );
  }

  public enableSkipPayment() {
    this.globalObject.ReviewConfiguration.isSkipPaymentEnabled = true;
  }

  public disableSkipPayment() {
    this.globalObject.ReviewConfiguration.isSkipPaymentEnabled = false;
  }

  private resetRetrievedExternalIntegrationData() {
    this.resetRetrievedEnvironmentalPermitIntegrationData();
  }

  private resetRetrievedEnvironmentalPermitIntegrationData() {
    if (
      this.globalObject.AttachmentsConfiguration?.ServiceExternalIntegration ==
        null ||
      this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration
        .envPermitLicenseDetailData == null
    ) {
      return;
    }

    this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration.envPermitLicenseDetailData =
      null;
    this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration.envPermitLicenseDetailId =
      null;
  }

  getServiceExternalIntegrationDataByRequestTransactionId(): void {
    let serviceRequestTransactionId: number = null;
    serviceRequestTransactionId = this.isServiceRequestTransactionInProgress()
      ? this.retrievedServiceRequestTransaction?.id
      : null;

    if (serviceRequestTransactionId == null) {
      return;
    }
    if (
      this.retrievedServiceRequestTransaction?.serviceExternalIntegrationData ==
      null
    ) {
      return;
    }

    this.getServiceExternalIntegrationService
      .GetServiceExternalIntegration(serviceRequestTransactionId)
      .subscribe((data) => {
        let envPermitLicenseDetailData =
          data.responseData.envPermitLicenseDetailData;
        this.globalObject.AttachmentsConfiguration.ServiceExternalIntegration =
          data.responseData;
      });
  }

  handleRequestPurposeTypeValidation(): void {
    if (
      this.utilService.isProfileHasResearchCenterType() &&
      this.requestTypeObject.serviceData?.isAllowingException
    ) {
      this.globalObject.ApplicantConfiguration.form
        .get('selectedRequestPurposeType')
        .setValidators([Validators.required]);
      this.globalObject.ApplicantConfiguration.form
        .get('selectedRequestPurposeType')
        .updateValueAndValidity();
      this.globalObject.ApplicantConfiguration.showRequestPurposeType = true;
    }
    if (this.isWaitingForCustomerAction() === true) {
      this.globalObject.ApplicantConfiguration.form
        .get('selectedRequestPurposeType')
        .disable();
    }
  }

  resetPaymentAmount(
    selectedRequestPurposeTypeCode: RequestPurposeTypeEnum
  ): void {
    if (selectedRequestPurposeTypeCode == null) {
      return;
    }

    if (
      this.submittedActionCode == WorkflowActionsCodesEnum.ReSubmitBYCustomer
    ) {
      return;
    }

    // this.globalObject.ServiceDetailsConfiguration.form.reset();
    if (!this.isStaticPayment) {
      this.reInitializeServiceDetail();
    }
    if (selectedRequestPurposeTypeCode == RequestPurposeTypeEnum.Commercial) {
      if (this.isStaticPayment) {
        this.feesFromPreviousYears =
          this.hasPaymentDto.staticRenewalFeesForPreviousYears;
        this.setTotalPayment(
          this.hasPaymentDto.staticServiceFees -
            this.hasPaymentDto.staticRenewalFeesForPreviousYears
        );
      }
    } else {
      if (this.isStaticPayment) {
        this.feesFromPreviousYears = 0;
        this.setTotalPayment(0);
      }
    }

    if (
      this.requestTypeCode == ServiceRequestTypeCodeEnum.Renew ||
      this.requestTypeCode == ServiceRequestTypeCodeEnum.CancelLicense
    ) {
      if (!this.isStaticPayment) {
        this.templateService
          .getRenewalFeesForPreviousYears(
            this.serviceId,
            selectedRequestPurposeTypeCode
          )
          .subscribe((response) => {
            this.feesFromPreviousYears = response.responseData;
          });
      }
    }
  }

  abstract reInitializeServiceDetail(): void;

  public async setFeedbackDone(): Promise<void> {}
}

export interface CongratsMessages {
  saveAndUpdate?: { key: string; keyArgs: any };
}
